import { ReactNode } from 'react';
import {
  AlertCircle,
  CheckCircle2,
  Info,
  X,
  TriangleAlert,
} from 'lucide-react';
import { cx } from './cx';
import { Icon } from './Icon';
import { Text } from './Text';
import { IconButton } from './IconButton';
import { useT } from '../../modules/i18n';

export type AlertSeverity = 'info' | 'success' | 'warning' | 'error';

const config: Record<AlertSeverity, { box: string; icon: ReactNode }> = {
  info: {
    box: 'bg-brand-solid/8 border-brand-solid/25 text-brand-text',
    icon: <Icon as={Info} size="lg" />,
  },
  success: {
    box: 'bg-success-500/10 border-success-500/25 text-success',
    icon: <Icon as={CheckCircle2} size="lg" />,
  },
  warning: {
    box: 'bg-warning-500/10 border-warning-500/25 text-warning',
    icon: <Icon as={TriangleAlert} size="lg" />,
  },
  error: {
    box: 'bg-error-500/10 border-error-500/25 text-error',
    icon: <Icon as={AlertCircle} size="lg" />,
  },
};

export interface AlertProps {
  severity?: AlertSeverity;
  title?: ReactNode;
  children?: ReactNode;
  onClose?: () => void;
  className?: string;
}

/**
 * Inline alert / banner — the shared message surface (replaces MUI lab Alert
 * in approvals and the scaffolder pickers). Severity maps to foundation
 * semantic colors; supports a title, body and optional dismiss.
 */
export const Alert = ({
  severity = 'info',
  title,
  children,
  onClose,
  className,
}: AlertProps) => {
  const t = useT();
  const c = config[severity];
  return (
    <div role="alert" className={cx('rounded-xl border p-4', c.box, className)}>
      <div className="flex items-center gap-2.5">
        <span className="shrink-0">{c.icon}</span>
        <div className="min-w-0 flex-1">
          {title ? (
            <Text variant="body" className="font-semibold text-fg">
              {title}
            </Text>
          ) : (
            children && (
              <Text variant="body" tone="secondary">
                {children}
              </Text>
            )
          )}
        </div>
        {onClose && (
          <IconButton
            aria-label={t('ui.alert.dismissAriaLabel')}
            size="sm"
            onPress={onClose}
            className="-my-1 -mr-1 shrink-0"
          >
            <Icon as={X} size="sm" />
          </IconButton>
        )}
      </div>
      {title && children && (
        <Text variant="body" tone="secondary" className="mt-1.5 pl-[30px]">
          {children}
        </Text>
      )}
    </div>
  );
};
